<template>
    <v-dialog v-model="localDialog" width="500">
        <v-card class="pa-2">
            <v-card-title>
                <h3>Confirm Location</h3>
            </v-card-title>
            <v-card-text>
                <div v-if="loading" class="text-center">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <p class="mt-3">Getting your current location...</p>
                    <p class="text-caption">Please allow location access when prompted</p>
                </div>
                <div v-else-if="error" class="text-center">
                    <v-icon color="error" size="48">mdi-map-marker-off</v-icon>
                    <p class="mt-3 error-text">{{ error }}</p>
                    <v-btn color="primary" variant="text" size="small" @click="retryLocation">
                        <v-icon size="small">mdi-refresh</v-icon> Try Again
                    </v-btn>
                </div>
                <div v-else-if="currentAddress" class="text-center">
                    <v-icon color="success" size="48">mdi-map-marker-check</v-icon>
                    <p class="mt-3">Your current location:</p>
                    <div class="address-preview pa-3 my-2">
                        <p style="color: #5c3a21;"><strong>{{ currentAddress }}</strong></p>
                    </div>
                    <p class="text-grey-darken-1">
                        When you confirm your current location, customers will be able to 
                        locate your store easily using integrated map. Do you want to continue?</p>
                </div>
            </v-card-text>
            <v-spacer></v-spacer>
            <div class="d-flex justify-space-between pa-3">
                <v-btn color="error" variant="tonal" @click="closeDialog">
                    <v-icon color="error">mdi-close-circle-outline</v-icon>&nbsp;
                    Close
                </v-btn>
                <v-btn 
                    color="primary" 
                    variant="tonal" 
                    @click="confirmLocation"
                    :disabled="loading || !!error"
                >
                    <v-icon color="primary">mdi-check</v-icon>&nbsp;Yes</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
    name: "LocationConfirmDialog",
    props: {
        modelValue: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'location-selected'],
    setup(props, { emit }) {
        const localDialog = computed({
            get: () => props.modelValue,
            set: (value) => emit('update:modelValue', value)
        });

        const loading = ref(false);
        const error = ref(null);
        const currentAddress = ref(null);
        const currentCoordinates = ref(null);

        // Reset state when dialog opens
        watch(localDialog, (newVal) => {
            if (newVal) {
                resetState();
                requestLocation();
            }
        });

        const resetState = () => {
            loading.value = false;
            error.value = null;
            currentAddress.value = null;
            currentCoordinates.value = null;
        };

        const requestLocation = () => {
            loading.value = true;
            error.value = null;
            currentAddress.value = null;

            // Check if geolocation is available
            if (!navigator.geolocation) {
                error.value = 'Geolocation is not supported by your browser';
                loading.value = false;
                return;
            }

            // Request location with high accuracy
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    currentCoordinates.value = { lat: latitude, lng: longitude };
                    
                    // Reverse geocode to get address
                    await reverseGeocode(latitude, longitude);
                },
                (err) => {
                    console.error('Location error:', err);
                    switch(err.code) {
                        case err.PERMISSION_DENIED:
                            error.value = 'Location access denied. Please enable location in your browser settings.';
                            break;
                        case err.POSITION_UNAVAILABLE:
                            error.value = 'Location information is unavailable.';
                            break;
                        case err.TIMEOUT:
                            error.value = 'Location request timed out. Please try again.';
                            break;
                        default:
                            error.value = 'An error occurred while getting your location.';
                    }
                    loading.value = false;
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        };

        const reverseGeocode = async (lat, lng) => {
            try {
                // Using Nominatim OpenStreetMap API for reverse geocoding
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
                    {
                        headers: {
                            'Accept-Language': 'en-US,en;q=0.9',
                            'User-Agent': 'Locinder Shop Application'
                        }
                    }
                );
                
                const data = await response.json();

                if (data && data.display_name) {
                    const address = data.address;
                    let formattedAddress = '';

                    // Format address components
                    if (address.road || address.pedestrian) {
                        formattedAddress += address.road || address.pedestrian || '';
                    }
                    if (address.suburb) {
                        formattedAddress += formattedAddress ? `, ${address.suburb}` : address.suburb;
                    }
                    if (address.city || address.town || address.village) {
                        const city = address.city || address.town || address.village;
                        formattedAddress += formattedAddress ? `, ${city}` : city;
                    }
                    if (address.state) {
                        formattedAddress += formattedAddress ? `, ${address.state}` : address.state;
                    }
                    if (address.postcode) {
                        formattedAddress += formattedAddress ? ` ${address.postcode}` : address.postcode;
                    }

                    currentAddress.value = formattedAddress || data.display_name.split(',')[0];
                } else {
                    // Fallback to coordinates if address not found
                    currentAddress.value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                }
                
                loading.value = false;
            } catch (err) {
                console.error('Reverse geocoding error:', err);
                // Fallback to coordinates
                currentAddress.value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                loading.value = false;
            }
        };

        const retryLocation = () => {
            requestLocation();
        };

        const confirmLocation = () => {
            if (currentAddress.value) {
                // Emit the selected address and coordinates
                emit('location-selected', {
                    address: currentAddress.value,
                    coordinates: currentCoordinates.value
                });
                closeDialog();
            }
        };

        const closeDialog = () => {
            resetState();
            localDialog.value = false;
        };

        return {
            localDialog,
            loading,
            error,
            currentAddress,
            requestLocation,
            retryLocation,
            confirmLocation,
            closeDialog
        };
    },
};
</script>

<style scoped>
h3 {
    font-size: clamp(1rem, 4vw, 1.5rem);
}

p {
    font-size: clamp(0.6rem, 4vw, 1rem);
    color: #808080;
}

.error-text {
    color: #d32f2f;
    font-size: 14px;
}

.address-preview {
    background: #f5f5f5;
    border-radius: 8px;
    word-break: break-word;
}

@media (max-width: 600px) {
    .to-show {
        display: inline;
    }
    .to-hide {
        display: none;
    }
}

@media (min-width: 601px) {
    .to-show {
        display: none;
    }
    .to-hide {
        display: inline;
    }
}
</style>